import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Radio,
  Button,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";

function Summary({ summaryData, selectedRecuperator, setSelectedRecuperator, priceCategory }) {
  const [isAddingToCart, setIsAddingToCart] = React.useState(false);
  
  const totalSupply = summaryData.totalSupply;
  const totalExhaust = summaryData.totalExhaust;
  const totalRecuperators = summaryData.recuperators.length;
  const totalAir = Math.max(totalSupply, totalExhaust);

  // Calculate total price for additional materials with prices
  const additionalMaterialsPrice = React.useMemo(() => {
    return summaryData.materials
      .filter(material => material.price !== null)
      .reduce((sum, material) => sum + (material.price * material.quantity), 0);
  }, [summaryData.materials]);

  // Calculate total price including selected recuperator
  const totalPrice = React.useMemo(() => {
    const recuperatorPrice = selectedRecuperator?.price || 0;
    return additionalMaterialsPrice + (isFinite(recuperatorPrice) ? recuperatorPrice : 0);
  }, [additionalMaterialsPrice, selectedRecuperator]);

  // For price category A, add 'su PVM' text
  const vatSuffix = priceCategory === 'A' ? ' su PVM' : ' be PVM';

  const isVisible =
    totalSupply > 0 && totalExhaust > 0 && totalRecuperators > 0;

  // Function to add items to cart via postMessage
  const handleAddToCart = () => {
    if (!selectedRecuperator) {
      alert('Prašome pasirinkti rekuperatorių');
      return;
    }

    if (isAddingToCart) {
      return;
    }

    const items = [];
    
    if (selectedRecuperator.good_id) {
      items.push({
        good_id: selectedRecuperator.good_id,
        quantity: 1
      });
    }

    const materialsToAdd = summaryData.materials
      .filter(material => material.good_id && material.price !== null);

    materialsToAdd.forEach(material => {
      items.push({
        good_id: material.good_id,
        quantity: material.quantity
      });
    });

    if (items.length === 0) {
      console.error('No items could be added to the cart.')
      return;
    }

    console.log('Sending items to the cart:', items);
    
    setIsAddingToCart(true);
    
    window.parent.postMessage({
      addToCart: items
    }, process.env.REACT_APP_PARENT_DOMAIN);
  };

  return (
    <Box 
      sx={{
        p: 2,
        pt: 0,
        "@media (max-width: 600px)": {
          p: 1,
          pt: 0,
        },
      }}
    >
      <Paper elevation={2} sx={{ 
        mb: 2, 
        p: 2,
        "@media (max-width: 600px)": {
          p: 1,
        },
      }}>
        <Typography variant="h6" gutterBottom>
          Oro kiekiai, viso
        </Typography>
        <Typography variant="body2">
          Paduodamo oro kiekis: {totalSupply} m³/h
        </Typography>
        <Typography variant="body2">
          Ištraukiamo oro kiekis: {totalExhaust} m³/h
        </Typography>
        <Typography variant="body2">
          Oro kiekis iš viso: {totalAir} m³/h
        </Typography>
      </Paper>

      {isVisible && (
        <Paper elevation={2} sx={{ mb: 2, p: 0 }}>
          <Typography variant="h6" gutterBottom sx={{ pl: 2, pt: 1 }}>
            Rekuperatoriai tinkami objektui
          </Typography>
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table
              size="small"
              sx={{
                "& .MuiTableCell-root": {
                  "@media (max-width: 600px)": {
                    fontSize: "0.7rem",
                    padding: "6px 4px",
                  },
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell align="center">Nuotrauka</TableCell>
                  <TableCell align="center" sx={{ "@media (max-width: 600px)": { display: "none" } }}>Kodas</TableCell>
                  <TableCell align="center">Aprašymas</TableCell>
                  <TableCell align="center">Kaina, €{vatSuffix}</TableCell>
                  <TableCell align="center">Pasirinkimas</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summaryData.recuperators.map((recuperator, index) => (
                  <TableRow
                    key={index}
                    hover
                    onClick={() => {
                      setSelectedRecuperator(selectedRecuperator?.code === recuperator.code ? null : recuperator);
                    }}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell align="center">
                      <img
                        src={recuperator.icon}
                        alt={recuperator.name}
                        style={{ width: "100px", height: "100px", maxWidth: "100%" }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ "@media (max-width: 600px)": { display: "none" } }}>{recuperator.code}</TableCell>
                    <TableCell align="center">{recuperator.name}</TableCell>
                    <TableCell align="center">
                      {recuperator.price ? recuperator.price.toFixed(2) : 'Nenurodyta'}
                    </TableCell>
                    <TableCell align="center">
                      <Radio
                        checked={selectedRecuperator?.code === recuperator.code}
                        onChange={(e) => {
                          e.stopPropagation();
                          setSelectedRecuperator(recuperator);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        value={recuperator.code}
                        name="recuperator-selection"
                        inputProps={{ 'aria-label': `Select ${recuperator.name}` }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {isVisible && (
        <Paper elevation={2} sx={{ p: 0 }}>
          <Typography variant="h6" gutterBottom sx={{ pl: 2, pt: 1 }}>
            Medžiagos, reikalingos objektui
          </Typography>
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table
              size="small"
              sx={{
                "& .MuiTableCell-root": {
                  "@media (max-width: 600px)": {
                    fontSize: "0.7rem",
                    padding: "6px 4px",
                  },
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ "@media (max-width: 600px)": { display: "none" } }}>Nuotrauka</TableCell>
                  <TableCell align="center" sx={{ "@media (max-width: 600px)": { display: "none" } }}>Kodas</TableCell>
                  <TableCell align="center">Aprašymas</TableCell>
                  <TableCell align="center">Kiekis</TableCell>
                  <TableCell align="center">Kaina, €{vatSuffix}</TableCell>
                  <TableCell align="center">Suma, €{vatSuffix}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summaryData.materials.map((material, index) => (
                  <TableRow key={index}>
                    <TableCell align="center" sx={{ "@media (max-width: 600px)": { display: "none" } }}>
                      <Avatar
                        src={material.icon}
                        alt={material.name}
                        sx={{ width: 32, height: 32 }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ "@media (max-width: 600px)": { display: "none" } }}>{material.code}</TableCell>
                    <TableCell align="center">{material.name}</TableCell>
                    <TableCell align="center">{material.quantity}</TableCell>
                    <TableCell align="right">{material.price !== null ? material.price.toFixed(2) : '-'}</TableCell>
                    <TableCell align="right">
                      {material.price !== null ? (material.price * material.quantity).toFixed(2) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pb: 2 }}>
            <TableContainer component={Paper} sx={{ width: 'fit-content', borderRadius: 0, mb: 0 }}>
              <Table size="small" sx={{ mb: 0 }}>
                <TableBody>
                  <TableRow>
                    <TableCell><b>Papildomų medžiagų kaina:</b></TableCell>
                    <TableCell align="right">{additionalMaterialsPrice.toFixed(2)} €{vatSuffix}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><b>Rekuperatoriaus kaina:</b></TableCell>
                    <TableCell align="right">
                      {selectedRecuperator
                        ? (selectedRecuperator.price
                          ? `${selectedRecuperator.price.toFixed(2)} €${vatSuffix}`
                          : 'Kaina nenurodyta')
                        : 'Nepasirinkta'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><b>Bendra suma:</b></TableCell>
                    <TableCell align="right"><b>{totalPrice.toFixed(2)} €{vatSuffix}</b></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, pb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<ShoppingCartIcon />}
              onClick={handleAddToCart}
              disabled={!selectedRecuperator || isAddingToCart}
              sx={{
                minWidth: '200px',
              }}
            >
              {isAddingToCart ? 'Pridėta į krepšelį' : 'Pridėti į krepšelį'}
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
}

export default Summary;
